import { X } from 'lucide-react'
import { observer } from 'mobx-react'
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'

import {
  INITIAL_LATEST_COUNT,
  progressStore,
  renderManager,
  uploadManager,
} from '@maxus/website/contexts'
import { useInfiniteScroll } from '@maxus/website/hooks'
import { trpcClient } from '@maxus/website/lib'
import { cn } from '@maxus/website/utils'

import { Button } from '../../base'
import { useDrawer } from '../../hooks/use-drawer'
import { useNotification } from '../notification'
import { VideoThumb } from '../video-thumb'

export type WorkflowPanelHandle = { open: () => void; close: () => void }

type WorkflowPanelProps = {
  // 关闭时回调（用于同步左侧导航的 Upload 高亮）。
  onClose?: () => void
}

const DAY_MS = 86_400_000

// 按创建时间分组：今天 / 昨天 / 最近 7 天 / 最近 30 天 / 更早（i18n key 即英文原文）。
const TIME_GROUPS = [
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'last7', label: 'Last 7 days' },
  { key: 'last30', label: 'Last 30 days' },
  { key: 'earlier', label: 'Earlier' },
] as const

type TimeGroupKey = (typeof TIME_GROUPS)[number]['key']

const startOfTodayMs = (): number => {
  const now = new Date()
  now.setHours(
    0,
    0,
    0,
    0,
  )
  return now.getTime()
}

const groupOf = (createdAt: number, todayStart: number): TimeGroupKey => {
  if (createdAt >= todayStart) return 'today'
  if (createdAt >= todayStart - DAY_MS) return 'yesterday'
  if (createdAt >= todayStart - 7 * DAY_MS) return 'last7'
  if (createdAt >= todayStart - 30 * DAY_MS) return 'last30'
  return 'earlier'
}

// 合并后的一行：面包屑/封面来自服务端 latestWorkflows context，状态/进度来自 workflows，stage 来自 workflowStageMap。
type PanelRow = {
  workflowId: string
  fileName: string
  breadcrumb: string[]
  coverUrl: string | null
  status: string
  progress: number
  stage: string | null
  stageProgress: number | null
  createdAt: number
  pendingFrontendRetry: {
    expectedAttempt: number
    taskId: string
    taskType: 'render' | 'upload'
  } | null
  retryCapability:
    | { allowed: false; reason: string }
    | {
        allowed: true
        attempt: number
        executor: 'server' | 'website'
        taskId: string
        taskType: string
      }
}

// 状态文案颜色（缺省回退 secondary）。
const STATUS_COLOR: Record<string, string> = {
  pending: 'text-text-secondary',
  processing: 'text-text-secondary',
  'retry-waiting': 'text-status-danger',
  succeeded: 'text-status-resolved',
  failed: 'text-status-danger',
  canceled: 'text-text-tertiary',
}

// stage → i18n key（Rendering / Uploading / Transcoding）。
const stageLabelKey = (stage: string): string =>
  stage
    .charAt(0)
    .toUpperCase() + stage.slice(1)

// 处理中且已算出 stage：展示 stage 进度；否则用 workflow 级进度。
const rowPercent = (row: PanelRow): number =>
  row.status === 'processing' && row.stage ? (row.stageProgress ?? 0) : row.progress

export const WorkflowPanel = observer(
  forwardRef<WorkflowPanelHandle, WorkflowPanelProps>(({ onClose }, ref) => {
    const { t } = useTranslation()
    const { notify } = useNotification()
    const {
      active,
      entered,
      closing,
      open,
      close,
    } = useDrawer()
    const [, setActionRevision] = useState(0)
    const [cancelRetryWorkflowIds, setCancelRetryWorkflowIds] = useState<Set<string>>(() => new Set())

    const handleClose = useCallback(
      () => {
        close()
        progressStore.latestCount = INITIAL_LATEST_COUNT
        onClose?.()
      },
      [close, onClose],
    )

    useImperativeHandle(
      ref,
      () => ({ open: () => open(), close: handleClose }),
      [open, handleClose],
    )

    // 无限滚动：哨兵进入 ScrollArea 视口即放大 latestCount（progressStore 据此拉更多）。
    const sentinelRef = useInfiniteScroll<HTMLDivElement>({
      enabled: progressStore.hasMoreWorkflows,
      observeKey: progressStore.latestWorkflows.length,
      onLoadMore: () => {
        progressStore.latestCount += INITIAL_LATEST_COUNT
      },
      getRoot: sentinel => sentinel.closest('[data-slot=workflow-panel-content]'),
    })

    // layout 卸载兜底：复位放大后的 latestCount，避免后台一直拉大列表。
    useEffect(
      () => () => {
        progressStore.latestCount = INITIAL_LATEST_COUNT
      },
      [],
    )

    const handleCancel = async (workflowId: string) => {
      // 先尽力中止本机在跑的上传/渲染，再由服务端原子取消整个 workflow 并清理其产物。
      await uploadManager
        .abort(workflowId)
        .catch(error => {
          console.error('[workflow-panel] abort upload error', error)
        })
      await renderManager
        .stop(workflowId)
        .catch(error => {
          console.error('[workflow-panel] stop render error', error)
          notify({
            status: 'error',
            title: t('Failed'),
            description: error instanceof Error ? error.message : undefined,
          })
        })
      try {
        await trpcClient.project.workflow.cancel.mutate({ workflowId })
        setCancelRetryWorkflowIds(current => {
          if (!current.has(workflowId)) return current
          const next = new Set(current)
          next.delete(workflowId)
          return next
        })
      } catch (error) {
        console.error('[workflow-panel] cancel error', error)
        setCancelRetryWorkflowIds(current => new Set(current).add(workflowId))
      }
    }

    const handlePauseToggle = async (workflowId: string) => {
      try {
        const changed = uploadManager.isPaused(workflowId)
          ? await uploadManager.resume(workflowId)
          : uploadManager.pause(workflowId)
        if (changed) setActionRevision(revision => revision + 1)
      } catch (error) {
        console.error('[workflow-panel] pause/resume error', error)
      }
    }

    const handleRetry = async (row: PanelRow) => {
      const capability = row.retryCapability.allowed ? row.retryCapability : null
      const retryTarget = capability ?? row.pendingFrontendRetry
      const expectedAttempt = capability?.attempt ?? row.pendingFrontendRetry?.expectedAttempt
      if (!retryTarget || expectedAttempt === undefined) return
      try {
        if (retryTarget.taskType === 'upload') {
          await uploadManager.retry(row.workflowId, retryTarget.taskId, expectedAttempt)
        } else if (retryTarget.taskType === 'render') {
          await renderManager.retry(row.workflowId, retryTarget.taskId, expectedAttempt)
        } else {
          await trpcClient.project.workflow.retryTask.mutate({
            workflowId: row.workflowId,
            taskId: retryTarget.taskId,
            expectedAttempt,
          })
        }
      } catch (error) {
        console.error('[workflow-panel] retry error', error)
        notify({
          status: 'error',
          title: t('Failed'),
          description: error instanceof Error ? t(error.message) : undefined,
        })
      } finally {
        setActionRevision(revision => revision + 1)
      }
    }

    if (!active) return null

    // 面板挂载即代表打开。合并三源为行：latestWorkflows(context+cover) × workflows(status/progress) × workflowStageMap(stage)。
    const workflowById = new Map(progressStore.workflows.map(workflow => [workflow.id, workflow]))
    const rows: PanelRow[] = progressStore.latestWorkflows.flatMap(context => {
      const workflow = workflowById.get(context.workflowId)
      if (!workflow) return []
      const stage = progressStore.workflowStageMap[context.workflowId]
      const pendingFrontendRetry =
        renderManager.getPendingRetry(context.workflowId) ?? uploadManager.getPendingRetry(context.workflowId) ?? null
      const breadcrumb = [
        context.projectName,
        context.deliverableName,
        context.stageName,
        context.platformName === 'default' ? null : context.platformName,
        `v${context.timelineVersionNumber}`,
      ].filter((segment): segment is string => Boolean(segment))

      return [
        {
          workflowId: context.workflowId,
          fileName: workflow.kind === 'review-master' ? t('Master') : workflow.assetName || t('Untitled asset'),
          breadcrumb,
          coverUrl: context.coverUrl,
          status: workflow.status,
          progress: workflow.progress,
          stage: workflow.status === 'processing' ? (stage?.stage ?? null) : null,
          stageProgress: stage?.progress ?? null,
          createdAt: new Date(workflow.createdAt).getTime(),
          pendingFrontendRetry,
          retryCapability: workflow.retryCapability,
        },
      ]
    })

    const todayStart = startOfTodayMs()
    const groups = TIME_GROUPS
      .map(group => ({
        ...group,
        items: rows
          .filter(row => groupOf(row.createdAt, todayStart) === group.key)
          .sort((a, b) => b.createdAt - a.createdAt),
      }))
      .filter(group => group.items.length > 0)

    return (
      <>
        {/* 遮罩：从侧栏右侧(left-20)开始，不遮挡、也不变暗左侧导航 */}
        <div
          className={cn(
            'fixed inset-0 left-20 z-40 bg-black/40 transition-colors',
            (!entered || closing) && 'bg-black/0',
          )}
          onPointerDown={handleClose}
        />
        {/* 定位层：左停靠 + 裁剪滑出 */}
        <div className="fixed inset-y-0 left-20 z-50 w-125 overflow-hidden">
          <div
            className={cn(
              'h-full w-full transition-transform duration-[240ms] ease-out',
              (!entered || closing) && '-translate-x-full',
            )}
            role="dialog"
          >
            <div className="flex h-full w-full flex-col border-r border-border-subtle bg-surface-panel">
              {/* header */}
              <div className="flex shrink-0 items-start justify-between border-b border-border-subtle px-6 pt-5 pb-4">
                <div className="flex flex-col gap-1">
                  <span className="text-base font-semibold text-text-primary">{t('Upload manager')}</span>
                  <span className="text-xs text-text-secondary">{t('View upload and decoding task status.')}</span>
                </div>
                <Button aria-label={t('Close panel')} onClick={handleClose} size="icon-sm" variant="ghost">
                  <X className="size-5" />
                </Button>
              </div>

              {/* body：flex 列里必须 min-h-0 + overflow-y-auto 才能正常滚动（镜像 mmam） */}
              <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5" data-slot="workflow-panel-content">
                {rows.length === 0 ? (
                  <div className="py-10 text-center text-sm text-text-tertiary">{t('No uploads yet')}</div>
                ) : (
                  <div className="space-y-8">
                    {groups.map(group => (
                      <section key={group.key}>
                        <h2 className="mb-4 text-sm text-text-tertiary">{t(group.label)}</h2>
                        <div className="space-y-6">
                          {group.items.map(row => (
                            <WorkflowRow
                              key={row.workflowId}
                              onCancel={() => handleCancel(row.workflowId)}
                              onPauseToggle={() => void handlePauseToggle(row.workflowId)}
                              onRetry={() => handleRetry(row)}
                              retryCancel={cancelRetryWorkflowIds.has(row.workflowId)}
                              row={row}
                            />
                          ))}
                        </div>
                      </section>
                    ))}
                  </div>
                )}
                {progressStore.hasMoreWorkflows && <div aria-hidden="true" className="h-px" ref={sentinelRef} />}
              </div>
            </div>
          </div>
        </div>
      </>
    )
  }),
)

WorkflowPanel.displayName = 'WorkflowPanel'

const WorkflowRow = ({
  onCancel,
  onPauseToggle,
  onRetry,
  retryCancel,
  row,
}: {
  onCancel: () => void
  onPauseToggle: () => void
  onRetry: () => void
  retryCancel: boolean
  row: PanelRow
}) => {
  const { t } = useTranslation()

  const inProgress = row.status === 'pending' || row.status === 'processing' || row.status === 'retry-waiting'
  const retryTarget = row.retryCapability.allowed ? row.retryCapability : row.pendingFrontendRetry
  const canRetry = retryTarget !== null
  const uploadPaused = uploadManager.isPaused(row.workflowId)
  const canPauseUpload = row.stage === 'uploading' && (uploadPaused || uploadManager.canPause(row.workflowId))

  const statusLabel = (() => {
    switch (row.status) {
      case 'pending':
        return t('Pending')
      case 'processing':
        return `${row.stage ? t(stageLabelKey(row.stage)) : t('Processing')} ${rowPercent(row)}%`
      case 'retry-waiting':
        return t('Waiting for retry')
      case 'succeeded':
        return t('Completed')
      case 'failed':
        return t('Processing failed')
      case 'canceled':
        return t('Canceled')
      default:
        return row.status
    }
  })()

  return (
    <div className="grid grid-cols-[52px_minmax(0,1fr)_54px] gap-3" data-slot="workflow-panel-row">
      <VideoThumb className="size-[52px]" compact src={row.coverUrl} />
      <div className="min-w-0">
        <div className="truncate text-sm font-semibold text-text-primary">{row.fileName}</div>
        {row.breadcrumb.length > 0 && (
          <div className="mt-1.5 flex items-center gap-1 truncate text-xs text-text-tertiary">
            {row.breadcrumb.map((segment, index) => (
              <span key={index} className="truncate">
                {index > 0 && <span className="mr-1">/</span>}
                {segment}
              </span>
            ))}
          </div>
        )}
        {row.status === 'processing' && (
          <div className="mt-2 h-1 w-full max-w-[235px] rounded-full bg-surface-active">
            <div className="h-full rounded-full bg-brand-primary" style={{ width: `${rowPercent(row)}%` }} />
          </div>
        )}
        <div className={cn('mt-1.5 text-xs', STATUS_COLOR[row.status] ?? 'text-text-secondary')}>{statusLabel}</div>
      </div>
      <div className="flex flex-col gap-2">
        {inProgress && !retryCancel && (
          <>
            {canPauseUpload && (
              <Button className="w-[54px]" onClick={onPauseToggle} size="sm" variant="secondary">
                {t(uploadPaused ? 'Resume' : 'Pause')}
              </Button>
            )}
            {/* 首次取消进行中的 workflow，同时停止本地上传/渲染并请求服务端取消。 */}
            <Button className="w-[54px]" onClick={onCancel} size="sm" variant="secondary">
              {t('Cancel')}
            </Button>
          </>
        )}
        {/* 服务端取消失败后的补偿重试；仍执行取消，不会重新创建 workflow。 */}
        {retryCancel && (
          <Button
            className="w-[54px] border-status-danger bg-status-danger/10 text-status-danger hover:bg-status-danger/15 hover:text-status-danger"
            onClick={onCancel}
            size="sm"
            title={t('Cancel')}
            variant="outline"
          >
            {t('Retry')}
          </Button>
        )}
        {/* 在原 workflow / task 上开启下一 attempt；File 丢失时由 uploadManager 拉起选择器。 */}
        {!retryCancel &&
          canRetry &&
          (row.status === 'retry-waiting' || (row.status === 'processing' && row.pendingFrontendRetry !== null)) && (
            <Button
              className="w-[54px] border-status-danger bg-status-danger/10 text-status-danger hover:bg-status-danger/15 hover:text-status-danger"
              onClick={onRetry}
              size="sm"
              variant="outline"
            >
              {t('Retry')}
            </Button>
          )}
      </div>
    </div>
  )
}
